import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CloseIcon, StarIcon, UploadCloudIcon } from '../../public/icons';
import { reviewService } from '../../services/reviewService';
import { useAuth } from '../../context/AuthContext';

/* ─────────────────────────── Inline Toast ─────────────────────────── */
const Toast = ({ show, message, onClose }) => {
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div className="fixed top-6 right-6 z-[10000] animate-[slideIn_0.3s_ease-out]">
      <div className="flex items-start gap-3 bg-white border-l-4 border-[#2F8F5B] shadow-lg px-5 py-4 max-w-sm">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E7F4ED] flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2F8F5B"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-[#1C2A46]">Review submitted</p>
          <p className="text-xs text-[#6B635D] mt-0.5">{message}</p>
        </div>
        <button
          onClick={onClose}
          className="text-[#6B635D] hover:text-[#1C2A46] p-1 -mr-1"
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────── Field Error ─────────────────────────── */
const FieldError = ({ children }) => {
  if (!children) return null;
  return (
    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      {children}
    </p>
  );
};

/* ─────────────────────────── Main Modal ─────────────────────────── */
const ReviewModal = ({ isOpen, onClose, product }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [rating, setRating] = useState(0);
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [reviewerEmail, setReviewerEmail] = useState('');

  // 🟢 Uploaded media: [{ url, publicId, type }]
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [serverError, setServerError] = useState('');

  const [touched, setTouched] = useState({ rating: false, text: false });
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Pre-fill name/email from logged-in user
  useEffect(() => {
    if (!isOpen) return;
    if (user) {
      setReviewerName(`${user.firstName || ''} ${user.lastName || ''}`.trim());
      setReviewerEmail(user.email || '');
    }
  }, [isOpen, user]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setRating(0);
      setReviewTitle('');
      setReviewText('');
      setMedia([]);
      setUploading(false);
      setTouched({ rating: false, text: false });
      setSubmitAttempted(false);
      setShowToast(false);
      setServerError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  /* ───────── Upload to Cloudinary on file select ───────── */
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    // Cap at 5 total
    const remaining = 5 - media.length;
    const toUpload = files.slice(0, remaining);

    // Validate type + size
    const valid = toUpload.filter((f) => {
      if (f.size > 10 * 1024 * 1024) return false;
      if (!f.type.startsWith('image/') && !f.type.startsWith('video/')) return false;
      return true;
    });

    if (valid.length !== toUpload.length) {
      setServerError(
        'Some files were skipped (must be image/video, max 10MB each).'
      );
    }

    setUploading(true);
    try {
      const uploaded = [];
      for (const file of valid) {
        const result = await reviewService.uploadImageToCloudinary(file);
        uploaded.push({
          url: result.url,
          publicId: result.publicId,
          type: result.type
        });
      }
      setMedia((prev) => [...prev, ...uploaded].slice(0, 5));
    } catch (err) {
      console.error('Upload failed:', err);
      setServerError('Failed to upload one or more files. Please try again.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const removeMedia = (index) => {
    setMedia((prev) => prev.filter((_, i) => i !== index));
  };

  const goToLogin = () => {
    onClose();
    navigate('/login');
  };

  const goToSignup = () => {
    onClose();
    navigate('/register');
  };

  /* ───────── Validation ───────── */
  const errors = {
    rating: rating < 1 ? 'Please select a star rating' : '',
    text:
      reviewText.trim().length === 0
        ? 'Review is required'
        : reviewText.trim().length < 10
        ? 'Review must be at least 10 characters'
        : ''
  };

  const showError = (field) => {
    const shouldShow = touched[field] || submitAttempted;
    return shouldShow ? errors[field] : '';
  };

  const isValid = !errors.rating && !errors.text;
  const isSubmitEnabled = isValid && !submitting && !uploading;

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    setServerError('');

    if (!user) return;
    if (!isValid) return;
    if (!product?.title) return;

    setSubmitting(true);

    try {
      const payload = {
        product: {
          id: product.id || product._id || null,
          title: product.title,
          image: product.image,
          slug: product.slug || null,
          price: product.price || 0,
          category: product.category || null,
          description: product.description || null,
          inStock: product.inStock ?? true,
          location: product.location || null,
          isCustom: product.isCustom ?? false
        },
        rating,
        title: reviewTitle.trim(),
        text: reviewText.trim(),
        reviewerName: reviewerName.trim(),
        reviewerEmail: reviewerEmail.trim(),
        media // 🟢 real Cloudinary media
      };

      const result = await reviewService.createReview(payload);

      if (result.success) {
        setToastMessage('Your review will appear once it has been approved.');
        setShowToast(true);
        setTimeout(() => onClose(), 2000);
      } else {
        setServerError(result.message || 'Failed to submit review.');
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Failed to submit review. Please try again.';
      setServerError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Toast
        show={showToast}
        message={toastMessage}
        onClose={() => setShowToast(false)}
      />

      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] lg:w-[50%] xl:w-[40%] bg-[#F9F6F2] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between pb-5 pt-4 border-b mx-6 border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">Write a Review</h2>
              <p className="text-[#6B635D] text-sm mt-1">
                {user
                  ? 'Leave a review for this product.'
                  : 'Sign in to share your experience with this product.'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB]"
            >
              <CloseIcon className="w-6 h-6 text-[#1C2A46]" />
            </button>
          </div>

          {/* NOT LOGGED IN */}
          {!user ? (
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 text-center">
              <div className="flex items-center gap-4 mb-8 bg-white border border-[#DBDBDB] p-4 w-full max-w-md">
                <img
                  src={product?.image || '/placeholder.jpg'}
                  alt={product?.title || 'Product'}
                  className="w-20 h-24 object-cover flex-shrink-0"
                />
                <div className="text-left">
                  <h3 className="text-base font-medium text-[#1C2A46] line-clamp-2">
                    {product?.title || 'Product'}
                  </h3>
                  <p className="text-xs text-[#6B635D] mt-1">
                    You're about to review this item.
                  </p>
                </div>
              </div>

              <div className="w-16 h-16 rounded-full bg-[#F4E7F1] flex items-center justify-center mb-5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#55000C"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>

              <h3 className="text-xl font-semibold text-[#1C2A46] mb-2">
                You need to be signed in
              </h3>
              <p className="text-sm text-[#6B635D] max-w-sm mb-8 leading-relaxed">
                Only registered customers can write reviews. Sign in to your
                account or create a new one — it takes less than a minute.
              </p>

              <div className="w-full max-w-sm space-y-3">
                <button
                  onClick={goToLogin}
                  className="w-full py-3 bg-[#D2A146] text-white font-medium hover:bg-[#C49660] transition-colors"
                >
                  Sign In
                </button>
                <button
                  onClick={goToSignup}
                  className="w-full py-3 border border-[#1C2A46] text-[#1C2A46] font-medium hover:bg-[#1C2A46] hover:text-white transition-colors"
                >
                  Create an Account
                </button>
                <button
                  onClick={onClose}
                  className="w-full py-2 text-sm text-[#6B635D] hover:text-[#1C2A46] transition-colors"
                >
                  Maybe later
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* LOGGED IN — form */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Product Info */}
                <div className="flex gap-4 mb-6">
                  <div className="overflow-hidden">
                    <img
                      src={product?.image || '/placeholder.jpg'}
                      alt={product?.title || 'Product'}
                      className="w-48 h-56 object-cover"
                    />
                  </div>
                  <div className="flex flex-col text-left items-start justify-center flex-1">
                    <h3 className="text-lg font-medium text-[#1C2A46] mb-2">
                      {product?.title || 'Product'}
                    </h3>

                    <div
                      className="flex mb-1"
                      onBlur={() =>
                        setTouched((t) => ({ ...t, rating: true }))
                      }
                    >
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => {
                            setRating(star);
                            setTouched((t) => ({ ...t, rating: true }));
                          }}
                          className="mr-1 focus:outline-none hover:scale-110 transition-transform"
                          aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                        >
                          <StarIcon
                            filled={star <= rating}
                            className="w-6 h-6"
                          />
                        </button>
                      ))}
                    </div>

                    {rating > 0 && (
                      <p className="text-sm text-[#6B635D]">{rating} / 5</p>
                    )}

                    <FieldError>{showError('rating')}</FieldError>
                  </div>
                </div>

                {/* Review Title */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Review Title"
                    value={reviewTitle}
                    onChange={(e) => setReviewTitle(e.target.value)}
                    maxLength={120}
                    className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                  />
                </div>

                {/* Review Text */}
                <div className="mb-4">
                  <textarea
                    placeholder="Write your review here...*"
                    value={reviewText}
                    rows={6}
                    onChange={(e) => {
                      if (e.target.value.length <= 2000) {
                        setReviewText(e.target.value);
                        setTouched((t) => ({ ...t, text: true }));
                      }
                    }}
                    onBlur={() => setTouched((t) => ({ ...t, text: true }))}
                    maxLength={2000}
                    className={`w-full p-3 bg-[#F5F5F5] border-2 ${
                      showError('text') ? 'border-red-400' : 'border-[#E4E4E4]'
                    } placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent resize-none`}
                  />

                  <FieldError>{showError('text')}</FieldError>

                  <p className="text-xs text-[#6B635D] mt-1 text-right">
                    {reviewText.length} / 2000
                  </p>
                </div>

                {/* 🟢 Media Upload */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                    Add Photos or Videos (optional)
                  </label>

                  {media.length < 5 && (
                    <label className="block border-2 border-dashed border-[#DBDBDB] bg-[#F5F5F5] p-6 text-center hover:border-[#D2A146] transition-colors cursor-pointer rounded">
                      <input
                        type="file"
                        multiple
                        accept="image/*,video/*"
                        onChange={handleFileUpload}
                        className="hidden"
                        disabled={uploading || submitting}
                      />
                      <div className="flex justify-center items-center mb-3">
                        {uploading ? (
                          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D2A146]" />
                        ) : (
                          <UploadCloudIcon className="w-10 h-10 text-gray-400" />
                        )}
                      </div>
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        {uploading
                          ? 'Uploading…'
                          : 'Click to upload photos or videos'}
                      </p>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, JPEG, MP4 (max 10MB each, up to 5 files)
                      </p>
                    </label>
                  )}

                  {/* Previews */}
                  {media.length > 0 && (
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      {media.map((m, idx) => (
                        <div
                          key={idx}
                          className="relative aspect-square border border-[#DBDBDB] overflow-hidden"
                        >
                          {m.type === 'video' ? (
                            <video
                              src={m.url}
                              className="w-full h-full object-cover"
                              muted
                            />
                          ) : (
                            <img
                              src={m.url}
                              alt={`Upload ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => removeMedia(idx)}
                            className="absolute top-1 right-1 w-6 h-6 bg-black bg-opacity-60 text-white rounded-full flex items-center justify-center text-xs hover:bg-opacity-90"
                            aria-label="Remove"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {media.length >= 5 && (
                    <p className="mt-2 text-xs text-[#6B635D]">
                      Maximum of 5 files reached.
                    </p>
                  )}
                </div>

                {/* Server-side error */}
                {serverError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm">
                    {serverError}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-[#DBDBDB]">
                <button
                  onClick={handleSubmit}
                  disabled={!isSubmitEnabled}
                  className={`w-full py-3 font-medium transition-colors ${
                    isSubmitEnabled
                      ? 'bg-[#D2A146] text-white hover:bg-[#C49660]'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {submitting
                    ? 'Submitting…'
                    : uploading
                    ? 'Uploading…'
                    : 'Submit Review'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ReviewModal;